import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { insertOpportunityNote } from './submissionApi';
import '../../../styles/SubmissionCenter.css';

/**
 * Right-rail panel shown when "View Notes" is clicked on an opportunity card —
 * backed by AMPNowcertsController's own opportunity-notes endpoint (distinct
 * from QuotePolicyDetailPanel's insured+policy note thread, which is a
 * different NowCerts feed keyed by insuredDatabaseId/policyDatabaseId instead
 * of the opportunity itself). List and compose styled the same as that panel's
 * own "Notes & communication" (.sc-panel__comm* avatar thread, .sc-panel__task-btn
 * / .sc-panel__note-form add-task toggle) so the two don't look like different apps.
 */

const AUTH_URL = process.env.REACT_APP_Auth_Url || '';

interface OpportunityNote {
  databaseId: string;
  subject: string;
  createDate: string;
  createdBy: string;
}

interface Props {
  opportunityId: string;
  // NowCerts' InsertNote requires a valid insured_database_id even for an
  // opportunity-scoped note — every opportunity is itself tied to one, same
  // as creating the opportunity did in the first place.
  insuredDatabaseId: string;
  // Optional back/close affordance — when this panel is shown in place of the
  // sidebar's default dashboard (SubmissionCenterSidebarContent), the caller
  // passes this to return `selected` to null and show the dashboard again.
  onClose?: () => void;
}

/** First-load placeholder — mirrors the real .sc-panel__comm shape (avatar +
 *  two text lines) so there's no layout shift once real notes replace it. */
const SkeletonNotes: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <ul className="sc-panel__comms sc-panel__comms--compact">
    {Array.from({ length: count }, (_, i) => (
      <li className="sc-panel__comm sc-panel__comm--sys" key={i}>
        <span className="sc-panel__comm-avatar" aria-hidden="true" />
        <span className="sc-panel__comm-body">
          <div className="sc__skel-bone" style={{ width: `${45 - (i % 2) * 8}%`, height: 10, marginBottom: 6 }} />
          <div className="sc__skel-bone" style={{ width: `${75 - (i % 3) * 10}%`, height: 11 }} />
        </span>
      </li>
    ))}
  </ul>
);

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'N';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtRelative(iso?: string): string {
  if (!iso) return '—';
  const hasTz = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

const OpportunityNotesPanel: React.FC<Props> = ({ opportunityId, insuredDatabaseId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notes, setNotes] = useState<OpportunityNote[]>([]);
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleAddNote = async () => {
    const subject = noteText.trim();
    if (!subject || saving) return;
    if (!insuredDatabaseId) {
      setSaveError('This opportunity has no linked account on file — can\'t add a note.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await insertOpportunityNote(opportunityId, insuredDatabaseId, subject);
      const currentUserName =
        [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') ||
        localStorage.getItem('userName') || 'You';
      setNotes((prev) => [
        { databaseId: `local-${Date.now()}`, subject, createDate: new Date().toISOString(), createdBy: currentUserName },
        ...prev,
      ]);
      setNoteText('');
      setAddingNote(false);
    } catch {
      setSaveError('Failed to add note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelNote = () => {
    setAddingNote(false);
    setNoteText('');
    setSaveError(null);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch(`${AUTH_URL}/api/AMPNowcerts/opportunity/${encodeURIComponent(opportunityId)}/notes`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        NCToken: localStorage.getItem('amsToken') || '',
      },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        setNotes(Array.isArray(data?.notes) ? data.notes : []);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [opportunityId]);

  return (
    <div className="sc-panel__section">
      {onClose && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button type="button" className="sc-panel__close" aria-label="Close notes" onClick={onClose}>
            <FiX size={14} />
          </button>
        </div>
      )}
      {loading ? (
        <SkeletonNotes />
      ) : error ? (
        <p className="sc-panel__note" style={{ fontSize: 'var(--sc-t1)' }}>Failed to load notes.</p>
      ) : notes.length === 0 ? (
        <p className="sc-panel__note">No notes yet.</p>
      ) : (
        <ul className="sc-panel__comms sc-panel__comms--compact">
          {notes.map((n) => (
            <li key={n.databaseId} className="sc-panel__comm sc-panel__comm--sys">
              <span className="sc-panel__comm-avatar">{initials(n.createdBy || 'Note')}</span>
              <span className="sc-panel__comm-body">
                <span className="sc-panel__comm-head">
                  <strong>{n.createdBy || 'Note'}</strong>
                  <span className="sc-panel__comm-time">{fmtRelative(n.createDate)}</span>
                </span>
                <span className="sc-panel__comm-text">{stripHtml(n.subject) || 'Untitled note'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!addingNote ? (
        <button
          type="button"
          className="sc-panel__note-save-btn sc-panel__note-save-btn--sm"
          style={{ marginTop: 10 }}
          onClick={() => setAddingNote(true)}
        >
          + Add note
        </button>
      ) : (
        <div className="sc-panel__note-form sc-panel__note-form--compact" style={{ marginTop: 10 }}>
          <textarea
            className="sc-panel__note-input"
            placeholder="Add a note…"
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={saving}
            autoFocus
          />
          {saveError && <p className="sc-panel__note-error">{saveError}</p>}
          <div className="sc-panel__note-actions">
            <button
              type="button"
              className="sc-panel__note-cancel-btn sc-panel__note-cancel-btn--sm"
              onClick={handleCancelNote}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sc-panel__note-save-btn sc-panel__note-save-btn--sm"
              onClick={handleAddNote}
              disabled={saving || !noteText.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityNotesPanel;
